/**
 * @file backdrop-summary.ts
 * @description Сводки групп вкладки «Оформление → Фон и рамка» (phase19.16,
 * SB122; uiux-settings-groups §5 шаг 3; SB120 этап 2 — числами,
 * uiux-sb120-stage2 §2): что видно у свёрнутой карточки «Картинка фона»,
 * «Рисунки», «Свечение», «Раскладка сайта» — коротко, со строчной.
 */

import { SITE_COLOR_ROLE_INFO, SITE_LIMITS, type SiteColorRef, type SitePageBgImage, type SiteTheme } from "@chaiberi/shared";

import { PAGE_BG_MODE_OPTIONS } from "./page-bg";
import { ANCHOR_NAMES } from "../site-builder/section-meta";
import { formatNumber } from "../site-fields/number-input";

/** «нет» / «заполнить экран · не на телефоне». */
export function pageBgSummary(image: SitePageBgImage | null): string {
  if (!image) return "нет";
  const mode = PAGE_BG_MODE_OPTIONS.find((o) => o.value === image.mode)?.label.toLowerCase() ?? "";
  return image.hideOnMobile ? `${mode} · не на телефоне` : mode;
}

const STEPS = [0, 50, 100] as const;

/** Место рисунка словами, если оно — одно из девяти; иначе «x 30 · y 70 %». */
export function placeText(point: { x: number; y: number }): string {
  const col = STEPS.indexOf(point.x as (typeof STEPS)[number]);
  const row = STEPS.indexOf(point.y as (typeof STEPS)[number]);
  if (col >= 0 && row >= 0) return ANCHOR_NAMES[row * 3 + col] ?? "в центре";
  return `x ${formatNumber(point.x, 2)} · y ${formatNumber(point.y, 2)} %`;
}

/** «без рисунков» / «2 из 3 · справа сверху». */
export function decorSummary(backdrop: SiteTheme["backdrop"]): string {
  if (backdrop.mode !== "images") return "без рисунков";
  const first = backdrop.images[0];
  const count = `${backdrop.images.length} из ${SITE_LIMITS.backdropImages}`;
  return first ? `${count} · ${placeText(first)}` : count;
}

/** Цвет ссылкой словом: роль — её название, свой — код. */
export function colorRefLabel(ref: SiteColorRef): string {
  return ref.kind === "role" ? SITE_COLOR_ROLE_INFO[ref.role].label.toLowerCase() : ref.color;
}

/** «нет» / «18 % · акцент». */
export function hazeSummary(haze: SiteTheme["backdrop"]["haze"]): string {
  if (haze.strength <= 0) return "нет";
  return `${formatNumber(haze.strength, 2)} % · ${haze.color ? colorRefLabel(haze.color) : "акцент"}`;
}

/** «во всю ширину» / «по центру 1600 · тень 50 %». */
export function frameSummary(frame: SiteTheme["frame"]): string {
  if (frame.mode === "full") return "во всю ширину";
  return `по центру ${frame.maxWidth}${frame.shadow > 0 ? ` · тень ${formatNumber(frame.shadow, 2)} %` : ""}`;
}
